/** Longest destination the API puts in a sign-in link. */
const MAX_LENGTH = 512;

const PROBE_ORIGIN = 'https://liftai.invalid';

/**
 * Routes a sign-in never lands on: the sign-in, sign-out and emailed-link
 * pages (`router.tsx`), and everything under `/auth/`. A destination there
 * could sign the user out, or open another account's reset link after they
 * signed in. Matched on whole path segments, so `/authors` is not blocked. The
 * API blocks the same list (native_auth.links.BLOCKED_REDIRECT_PATHS).
 */
const BLOCKED_PATHS = ['/auth', '/login', '/logout', '/magic-link', '/reset-password', '/signup'];

const MALFORMED_ESCAPE = /%(?![0-9A-Fa-f]{2})/;

const hasUnsafeCharacter = (value: string) => /[\\#\s\p{C}]/u.test(value);

/**
 * `path` the way the router matches it, or null if it cannot be decoded. The
 * router percent-decodes a path and ignores case, so `/%61uth` and `/Auth`
 * reach `/auth`. Decoding repeats until the path stops changing, so a
 * double-encoded path is judged by what it finally becomes. Repeated slashes
 * collapse, `.` and `..` segments resolve, and a trailing slash drops.
 */
const normalisedPath = (raw: string): string | null => {
  let path = raw;
  let stable = false;
  for (let attempt = 0; attempt < 4 && !stable; attempt += 1) {
    if (MALFORMED_ESCAPE.test(path)) {
      return null;
    }
    let decoded: string;
    try {
      decoded = decodeURIComponent(path);
    } catch {
      return null;
    }
    stable = decoded === path;
    path = decoded;
  }
  if (!stable || hasUnsafeCharacter(path)) {
    return null;
  }
  const segments: string[] = [];
  for (const segment of path.toLowerCase().split('/')) {
    if (segment === '..') {
      segments.pop();
    } else if (segment !== '' && segment !== '.') {
      segments.push(segment);
    }
  }
  return `/${segments.join('/')}`;
};

/**
 * `value` if it is a path on this site that a sign-in may land on, else null.
 * The same rule the API applies before putting a destination in a sign-in
 * link (native_auth.links.safe_redirect_path): one leading `/` but not `//`,
 * no backslash, `#`, whitespace or control character, at most 512 characters,
 * and a decoded, normalised path outside the blocked routes above. Browsers
 * read `//host` and `/\host` as another host, and drop tabs and newlines from
 * a URL before parsing it; a fragment could carry another link's token. No
 * dependencies, so `linkToken.ts` can import it.
 */
export const safeRedirectPath = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value || value.length > MAX_LENGTH) {
    return null;
  }
  if (!value.startsWith('/') || value.startsWith('//') || hasUnsafeCharacter(value)) {
    return null;
  }
  if (new URL(value, PROBE_ORIGIN).origin !== PROBE_ORIGIN) {
    return null;
  }
  const path = normalisedPath(value.split('?')[0]);
  if (
    path === null ||
    BLOCKED_PATHS.some((blocked) => path === blocked || path.startsWith(`${blocked}/`))
  ) {
    return null;
  }
  return value;
};
